import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, DownloadSimple, Scroll } from "@phosphor-icons/react";
import { portalApi } from "@/lib/portalApi";
import { formatApiError } from "@/lib/api";
import { BRAND } from "./portalBrand";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function statusChip(s) {
  const x = (s || "").toLowerCase();
  if (x === "paid") return "text-emerald-700 border-emerald-200 bg-emerald-50";
  if (x.includes("partial")) return "text-amber-700 border-amber-200 bg-amber-50";
  return "text-rose-700 border-rose-200 bg-rose-50";
}

function Skeleton() {
  return <div className="space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-12 bg-white border border-slate-200 rounded-lg animate-pulse" />)}</div>;
}

export default function PortalInvoices() {
  const [tab, setTab] = useState("invoices");
  const [data, setData] = useState(null);
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [inv, stmt] = await Promise.all([
          portalApi.get("/portal/invoices"),
          portalApi.get("/portal/statement").catch(() => ({ data: null })),
        ]);
        setData(inv.data); setStatement(stmt.data);
      } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
    })();
  }, []);

  const downloadStatementCsv = () => {
    if (!statement?.events?.length) return;
    const rows = [["Date", "Type", "Particulars", "Debit", "Credit", "Balance"]];
    statement.events.forEach((e) => rows.push([e.date, e.type, `"${(e.particulars || "").replace(/"/g, '""')}"`, e.debit || "", e.credit || "", e.balance]));
    rows.push(["", "", "Closing balance", "", "", statement.closing_balance_inr]);
    const csv = rows.map((r) => r.join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `Statement_${BRAND.name}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Invoices &amp; Statement</h1>
          <p className="text-slate-500 text-sm mt-1">Your billing with {BRAND.name}.</p>
        </div>
        {data && (
          <div className="bg-white border border-slate-200 rounded-xl px-5 py-3 text-right shadow-sm">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Total outstanding</div>
            <div className={`text-2xl font-bold ${data.outstanding_inr > 0 ? "text-rose-600" : "text-emerald-600"}`}>{inr(data.outstanding_inr)}</div>
          </div>
        )}
      </div>

      <div className="inline-flex bg-white border border-slate-200 rounded-lg p-1 mb-4">
        <button onClick={() => setTab("invoices")} className={`px-3 py-1.5 text-sm rounded-md inline-flex items-center gap-1.5 ${tab === "invoices" ? "bg-blue-700 text-white" : "text-slate-600 hover:text-slate-900"}`}><Receipt size={14} /> Invoices</button>
        <button onClick={() => setTab("statement")} className={`px-3 py-1.5 text-sm rounded-md inline-flex items-center gap-1.5 ${tab === "statement" ? "bg-blue-700 text-white" : "text-slate-600 hover:text-slate-900"}`}><Scroll size={14} /> Statement</button>
      </div>

      {loading ? <Skeleton /> : tab === "invoices" ? (
        (data?.invoices || []).length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl py-14 text-center text-slate-500">No invoices yet.</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Invoice #</th>
                    <th className="px-4 py-2.5 text-left">Date</th>
                    <th className="px-4 py-2.5 text-left">Job</th>
                    <th className="px-4 py-2.5 text-right">Total</th>
                    <th className="px-4 py-2.5 text-right">Paid</th>
                    <th className="px-4 py-2.5 text-right">Balance</th>
                    <th className="px-4 py-2.5 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.invoices.map((i) => (
                    <tr key={i.invoice_number} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 font-mono font-semibold text-slate-800">{i.invoice_number}</td>
                      <td className="px-4 py-2.5 text-slate-600">{i.invoice_date}{i.due_date ? <span className="text-slate-400 text-xs"> · due {i.due_date}</span> : null}</td>
                      <td className="px-4 py-2.5 font-mono text-slate-500 text-xs">{i.job_number || "—"}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-800">{inr(i.total)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{inr(i.paid_inr)}</td>
                      <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${i.balance > 0 ? "text-rose-600" : "text-slate-400"}`}>{inr(i.balance)}</td>
                      <td className="px-4 py-2.5"><span className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border ${statusChip(i.status)}`}>{i.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-sm font-semibold text-slate-800">Statement of Account</div>
            <button onClick={downloadStatementCsv} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-300 text-slate-600 hover:bg-blue-50 hover:text-blue-700 text-xs"><DownloadSimple size={14} /> Export CSV</button>
          </div>
          {(statement?.events || []).length === 0 ? (
            <div className="py-14 text-center text-slate-500">No transactions yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Date</th>
                    <th className="px-4 py-2.5 text-left">Particulars</th>
                    <th className="px-4 py-2.5 text-right">Debit</th>
                    <th className="px-4 py-2.5 text-right">Credit</th>
                    <th className="px-4 py-2.5 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {statement.events.map((e, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{e.date}</td>
                      <td className="px-4 py-2.5 text-slate-700">{e.particulars}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-700">{e.debit ? inr(e.debit) : "—"}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-emerald-700">{e.credit ? inr(e.credit) : "—"}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-slate-800">{inr(e.balance)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-slate-200">
                    <td colSpan={4} className="px-4 py-2.5 text-right font-semibold text-slate-700">Closing balance</td>
                    <td className={`px-4 py-2.5 text-right font-bold ${statement.closing_balance_inr > 0 ? "text-rose-600" : "text-emerald-600"}`}>{inr(statement.closing_balance_inr)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
